<template>
  <div class="container d-flex flex-column">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <o-bread-crumb class="mb-0 flex-grow-1">
        <template v-slot:pageName>
          Dashboard
        </template>
        <template v-slot:pageDescription>
          <span v-if="page.output && businessProfile.merchant_id && !businessProfile.branch_id && !businessProfile.corporate_id">
            Insight of your business performance.
          </span>
          <span v-if="page.output && (businessProfile.branch_id || businessProfile.corporate_id)">
            Switch to branch / merchant for profile data.
          </span>
          <span v-if="page.output && (!businessProfile.merchant_id && !businessProfile.branch_id && !businessProfile.corporate_id)">
            Switch to corporate / branch / merchant for profile data.
          </span>
        </template>
      </o-bread-crumb>
      
      <!-- Desktop Segmented Radio Control -->
      <div v-if="page.output" class="d-none d-md-flex flex-column align-items-end" style="margin-top: -10px; padding-right: 24px;">
        <label class="small fw-bold mb-1 text-end" style="font-size: 0.725rem; letter-spacing: 0.05em; color: #334155;">
          <i class="fa-solid fa-calendar-days me-1" style="color: var(--primary-color, #F27178);"></i> TIMEFRAME
        </label>
        <div class="segmented-control">
          <button type="button" class="segment-btn" :class="{ active: timeframe === 'today' }" @click="timeframe = 'today'">Today</button>
          <button type="button" class="segment-btn" :class="{ active: timeframe === 'this_week' }" @click="timeframe = 'this_week'">This Week</button>
          <button type="button" class="segment-btn" :class="{ active: timeframe === 'this_month' }" @click="timeframe = 'this_month'">This Month</button>
          <button type="button" class="segment-btn" :class="{ active: timeframe === 'this_year' }" @click="timeframe = 'this_year'">This Year</button>
        </div>
      </div>

      <!-- Mobile/Tablet Dropdown Control -->
      <div v-if="page.output" class="d-md-none d-block" style="min-width: 120px; margin-top: -15px;">
        <label class="small fw-semibold d-block mb-1 text-end" style="font-size: 0.725rem; letter-spacing: 0.05em; color: #334155;">
          <i class="fa-solid fa-calendar-days me-1" style="color: var(--primary-color, #F27178);"></i> TIMEFRAME
        </label>
        <select class="form-select filter-selector shadow-sm" v-model="timeframe" style="font-size: 0.85rem; border-radius: 8px; border: 1px solid #cbd5e1; padding: 6px 32px 6px 12px; height: 38px; background-color: #ffffff;">
          <option value="today">Today</option>
          <option value="this_week">This Week</option>
          <option value="this_month">This Month</option>
          <option value="this_year">This Year</option>
        </select>
      </div>
    </div>
    <div class="d-flex justify-content-center spinner-border primary-icon spinner-position" v-if="page.isLoading">
      Loading...
    </div>
    <div class="spinner" v-if="page.isLoading"></div>
    <div v-if="page.output">
      <desktop :gain="gain" :dashboardMode="dashboardMode" :externalTimeframe="timeframe"></desktop>
      <mobile :gain="gain" :dashboardMode="dashboardMode" :externalTimeframe="timeframe"></mobile>
    </div>
  </div>
</template>

<script>
import store from '@/store/'
import { mapState } from 'vuex'
import Desktop from './Home/Desktop.vue'
import Mobile from './Home/Mobile.vue'
import { OBreadCrumb } from '@/components/OmnihControl'
import { computed, onMounted, reactive, ref, watch } from 'vue'

export default {
  setup() {
    let page = reactive({
      isLoading: false,
      output: false
    });

    let businessProfile = ref(null);
    let dataChanged = ref(false);
    let previousGain = ref(null);
    const dashboardMode = ref('');
    const timeframe = ref('this_month');

    let gain = computed(() => store.state.Dashboard.dashboards);

    onMounted(() => {
      load_state()
    });

    watch(gain, (newGain) => {
      if (JSON.stringify(newGain) !== JSON.stringify(previousGain.value)) {
        dataChanged.value = true;
        previousGain.value = newGain;
      }
    }, { immediate: true, deep: true });

    function load_state() {
      page.isLoading = true;

      store.dispatch('Auth/getBusinessProfile')
        .then((profile) => {
          businessProfile.value = profile;
          const selectedBusinessProfile = JSON.parse(localStorage.getItem('selected_business_profile'));
          const corporateId = selectedBusinessProfile ? selectedBusinessProfile.corporate_id : null;
          
          fetchTheme(corporateId);
          const branch_id = profile.branch_id;
          const merchant_id = profile.merchant_id;
          const corporate_id = profile.corporate_id;


          let data = {};

          if (branch_id && merchant_id) {
            data = { merchant_id };
            dashboardMode.value = 'merchant';
          } else if (branch_id) {
            data = { branch_id };
            dashboardMode.value = 'branch';
          } else if (merchant_id) {
            data = { merchant_id };
            dashboardMode.value = 'merchant';
          } else if (corporate_id) {
            data = { corporate_id };
            dashboardMode.value = 'corporate';
          } else {
            data = {};
            dashboardMode.value = '';
          }


          if (Object.keys(data).length !== 0) {
            store.dispatch('Dashboard/read', data)
               .catch((err) => {
                 console.error('Failed to load dashboard data:', err);
               });
          } else {
            // Mode admin - load admin summary instead of clearing
            store.dispatch('Dashboard/read', {})
              .then(() => {
                dataChanged.value = true;
              })
              .catch((err) => {
                console.error('Failed to load admin dashboard data:', err);
                dataChanged.value = true;
              });
          }
        })
        .finally(() => {            
          page.isLoading = false;
          page.output = true;
        });
    }

    function fetchTheme(corporateId) {
      if (corporateId) {
        store.dispatch('ThemeCorporate/read_single', corporateId)
          .then(() => {
            const themeData = store.state.ThemeCorporate;
            // Set properties CSS
            if (themeData.themeCorporate && Object.keys(themeData.themeCorporate).length > 0) {
              document.documentElement.style.setProperty('--top', themeData.themeCorporate.top_navbar);
              document.documentElement.style.setProperty('--sidebar', themeData.themeCorporate.sidebar_navbar);
              document.documentElement.style.setProperty('--bottom', themeData.themeCorporate.bottom_navbar);
              document.documentElement.style.setProperty('--text-navbar', themeData.themeCorporate.text_navbar);
              document.documentElement.style.setProperty('--primary-color', themeData.themeCorporate.primary_button);
            } else {
              document.documentElement.style.setProperty('--top', '#FC6666');
              document.documentElement.style.setProperty('--sidebar', '#CB5B5B');
              document.documentElement.style.setProperty('--bottom', '#663434');
              document.documentElement.style.setProperty('--text-navbar', '#FFFFFF');
              document.documentElement.style.setProperty('--primary-color', '#F27178');
            }
          })
          .catch(error => {
            console.error('Error fetching theme:', error);
          });
      }
    }

    return {
      page,
      load_state,
      fetchTheme,
      gain,
      businessProfile,
      dataChanged,
      dashboardMode,
      timeframe
    };
  },
  computed: {
    ...mapState({
      dashboards: (state) => state.Dashboard.dashboards
    }),
    needsReRender() {
      return this.$store.state.ReloadHome.needsReRender;
    }
  },
  watch: {
    needsReRender(newValue) {
      if (newValue) {
        this.reloadComponent();
        this.$store.commit('ReloadHome/setNeedsReRender', false);
      }
    }
  },
  methods: {
    reloadComponent() {
      this.load_state();
    }
  },
  components: {
    OBreadCrumb,
    Desktop,
    Mobile
  }
}
</script>

<style scoped>
.segmented-control {
  display: flex;
  background-color: #ffffff;
  padding: 3px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
}
.segment-btn {
  background: transparent;
  border: none;
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
  padding: 6px 16px;
  border-radius: 8px;
  transition: all 0.15s ease-in-out;
  outline: none;
}
.segment-btn.active {
  background-color: var(--primary-color, #F27178);
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
}
.segment-btn:hover:not(.active) {
  color: #0f172a;
}
</style>